import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../lib/auth";

// Detect environment for the badge in the admin header
export function detectEnv() {
  if (typeof window === "undefined") return { label: "Server", color: "#666" };
  const h = window.location.hostname;
  if (h === "gtawholesalesupply.ca" || h === "www.gtawholesalesupply.ca") {
    return { label: "Production", color: "#0047FF", live: true };
  }
  if (h.includes("preview.emergentagent.com") || h.includes("preview")) {
    return { label: "Preview", color: "#E63946", live: false };
  }
  if (h === "localhost" || h.startsWith("127.")) {
    return { label: "Local", color: "#666", live: false };
  }
  return { label: h, color: "#666", live: false };
}

export function Shell({ children, active }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const env = detectEnv();
  const groups = [
    { label: "Dashboard", items: [["overview", "Overview", "/admin"]] },
    { label: "Catalogue", items: [
      ["products", "Products", "/admin/products"],
      ["lowstock", "Low stock ⚑", "/admin/low-stock"],
      ["expiring", "Expiring soon", "/admin/expiring"],
      ["categories", "Categories", "/admin/categories"],
      ["brands", "Brands (optional)", "/admin/brands"],
      ["reviews", "Reviews", "/admin/reviews"],
    ]},
    { label: "Orders", items: [
      ["orders", "Orders", "/admin/orders"],
    ]},
    { label: "Leads & Accounts", items: [
      ["retailers", "Retailers", "/admin/retailers"],
      ["enquiries", "Enquiries", "/admin/enquiries"],
      ["inquiries", "Contact msgs", "/admin/inquiries"],
    ]},
    { label: "Site", items: [["settings", "Website Settings", "/admin/settings"]] },
  ];
  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col md:flex-row">
      <button className="md:hidden bg-ink text-paper px-4 py-3 flex items-center justify-between" onClick={() => setOpen(!open)} data-testid="admin-mobile-menu">
        <div className="flex items-center gap-2">
          <span className="font-display uppercase">{active}</span>
          <span className="border px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest"
                style={{ borderColor: env.color, color: env.color }}>{env.label}</span>
        </div>
        <span>{open ? "×" : "≡"}</span>
      </button>
      <aside className={`${open ? "block" : "hidden"} md:flex md:w-64 flex-col bg-ink text-paper md:sticky md:top-0 md:h-screen md:overflow-y-auto`}>
        <div className="p-6 border-b border-white/15 hidden md:block">
          <Link to="/" className="font-display text-2xl leading-none uppercase">GTA Wholesale Supply</Link>
          <div className="mt-2 flex items-center gap-2">
            <span className="overline text-soda">Admin</span>
            <span
              className="inline-flex items-center gap-1 border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest"
              style={{ borderColor: env.color, color: env.color }}
              data-testid="env-badge"
              title={`You are editing the ${env.label} environment (${window.location.hostname})`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: env.color }} />
              {env.label}
            </span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-5 text-sm">
          {groups.map(g => (
            <div key={g.label}>
              <div className="overline text-white/40 px-4 mb-2 text-[9px]">{g.label}</div>
              <div className="space-y-1">
                {g.items.map(([k, l, to]) => (
                  <Link key={k} to={to} data-testid={`admin-nav-${k}`} onClick={() => setOpen(false)}
                    className={`block px-4 py-2.5 uppercase text-xs tracking-[0.16em] font-bold border ${active === k ? "bg-paper text-ink border-paper" : "border-transparent hover:border-white/30"}`}>
                    {l}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/15 text-xs">
          <div className="text-white/60 mb-1 truncate">{user?.email}</div>
          <button onClick={() => { logout(); nav("/"); }} className="btn-ghost w-full" style={{ color: "#F4F4F0", borderColor: "#F4F4F0" }} data-testid="admin-logout">Logout</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

export function StatCard({ label, value, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="border border-ink p-5 md:p-6" data-testid={testid}>
      <div className="overline text-black/50 mb-3">{label}</div>
      <div className="font-display text-4xl md:text-5xl leading-none">{value}</div>
    </motion.div>
  );
}
